import { ObjectTools } from '../../tools/object-tools';
import { SolImage } from './sol-image';
import { NavigationTarget } from '../../tools/router-tools';

export interface SolContentRouteLinkTarget {
  readonly type: 'route';
  readonly routeId: string;
  readonly fragment: string | undefined;
}

export interface SolContentUrlLinkTarget {
  readonly type: 'url';
  readonly url: string;
}

export interface SolContentAnchorLinkTarget {
  readonly type: 'anchor';
  readonly anchorId: string;
}

export type SolContentLinkTarget = SolContentRouteLinkTarget | SolContentUrlLinkTarget | SolContentAnchorLinkTarget;

export interface SolContentLinkClickEvent {
  readonly target: SolContentLinkTarget;
  readonly navigationTarget: NavigationTarget;
}

export interface SolContentTextInline {
  readonly id: string;
  readonly type: 'text';
  readonly text: string;
}

export interface SolContentStrongInline {
  readonly id: string;
  readonly type: 'strong';
  readonly segments: readonly SolContentInline[];
}

export interface SolContentEmphasisInline {
  readonly id: string;
  readonly type: 'emphasis';
  readonly segments: readonly SolContentInline[];
}

export interface SolContentCodeInline {
  readonly id: string;
  readonly type: 'code';
  readonly text: string;
}

export interface SolContentLinkInline {
  readonly id: string;
  readonly type: 'link';
  readonly segments: readonly SolContentInline[];
  readonly target: SolContentLinkTarget;
}

export interface SolContentImageInline {
  readonly id: string;
  readonly type: 'image';
  readonly image: SolImage;
  readonly title: string | undefined;
}

export type SolContentInline =
  | SolContentTextInline
  | SolContentStrongInline
  | SolContentEmphasisInline
  | SolContentCodeInline
  | SolContentLinkInline
  | SolContentImageInline;

export interface SolContentHeadingBlock {
  readonly id: string;
  readonly type: 'heading';
  readonly level: 1 | 2 | 3 | 4 | 5 | 6;
  readonly anchorId: string;
  readonly segments: readonly SolContentInline[];
  readonly text: string;
}

export interface SolContentParagraphBlock {
  readonly id: string;
  readonly type: 'paragraph';
  readonly segments: readonly SolContentInline[];
}

export interface SolContentQuoteBlock {
  readonly id: string;
  readonly type: 'quote';
  readonly segments: readonly SolContentInline[];
}

export interface SolContentListItem {
  readonly id: string;
  readonly segments: readonly SolContentInline[];
  readonly childList: SolContentListBlock | undefined;
}

export interface SolContentListBlock {
  readonly id: string;
  readonly type: 'list';
  readonly ordered: boolean;
  readonly items: readonly SolContentListItem[];
}

export interface SolContentCodeBlock {
  readonly id: string;
  readonly type: 'code';
  readonly code: string;
  readonly language: string | undefined;
}

export interface SolContentTableBlock {
  readonly id: string;
  readonly type: 'table';
  readonly assetBasePath: string | undefined;
  readonly columns: readonly string[];
  readonly rows: readonly string[][];
}

export interface SolContentImageBlock {
  readonly id: string;
  readonly type: 'image';
  readonly image: SolImage;
  readonly title: string | undefined;
}

export interface SolContentDividerBlock {
  readonly id: string;
  readonly type: 'divider';
}

export type SolContentBlock =
  | SolContentHeadingBlock
  | SolContentParagraphBlock
  | SolContentQuoteBlock
  | SolContentListBlock
  | SolContentCodeBlock
  | SolContentTableBlock
  | SolContentImageBlock
  | SolContentDividerBlock;

export class SolContent {
  private constructor(public readonly blocks: readonly SolContentBlock[]) {}

  public static empty(): SolContent {
    return new SolContent([]);
  }

  public static of(blocks: readonly SolContentBlock[]): SolContent {
    return new SolContent(blocks);
  }

  public withBlocks(blocks: readonly SolContentBlock[]): SolContent {
    if (ObjectTools.equals(this.blocks, blocks)) {
      return this;
    }
    return this.copy({ blocks });
  }

  private copy(props: Partial<SolContent>): SolContent {
    return ObjectTools.copy<SolContent>(this, props);
  }
}
